import { Injectable, inject } from '@angular/core';
import {
  OrdersCountMetadata,
  OrdersService,
} from '@compumark/order-management-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OrderSelectionService {
  private orderService = inject(OrdersService);

  public selectAllChange$ = new BehaviorSubject<boolean>(false);
  public singleSelectionChange$ = new BehaviorSubject<boolean>(null as any);
  public selectedRowsCountChange$ = new BehaviorSubject<number>(0);

  private totalCount = 0;
  private selectedRowsCount = 0;
  private currentStatus: 'active' | 'inactive' = 'active';
  private dataLoaded = false;
  private filteredTotal: number | null = null;

  public changeOrderSelection(
    orderId: string,
    selected: boolean,
  ): Observable<number> {
    return this.orderService
      .singleSelectionChange(
        orderId,
        this.currentStatus as 'active' | 'inactive',
        selected,
      )
      .pipe(
        tap(() => {
          this.updateSingleSelectedRowsCount(selected);
          this.selectAllChange$.next(this.checkIfAllRowsAreSelected());
        }),
      );
  }

  public changeOrdersSelection(
    selected: boolean,
    orderIds: Array<string>,
  ): Observable<number> {
    return this.orderService
      .bulkSelectionChange(
        this.currentStatus as 'active' | 'inactive',
        orderIds,
      )
      .pipe(
        tap(() => {
          this.selectedRowsCount = selected ? orderIds.length : 0;
          this.selectedRowsCountChange$.next(this.selectedRowsCount);
          this.singleSelectionChange$.next(selected);
          this.selectAllChange$.next(selected);
        }),
      );
  }

  public update(
    ordersCountMetadata: OrdersCountMetadata,
    status: 'active' | 'inactive',
  ): void {
    this.dataLoaded = false;
    this.filteredTotal = null;
    this.currentStatus = status;
    this.singleSelectionChange$.next(null as any);
    this.totalCount = ordersCountMetadata?.[this.currentStatus]?.total ?? 0;
    this.selectedRowsCount =
      ordersCountMetadata?.[this.currentStatus]?.selected ?? 0;
    this.selectAllChange$.next(this.checkIfAllRowsAreSelected());
    this.selectedRowsCountChange$.next(this.selectedRowsCount);
  }

  private checkIfAllRowsAreSelected(): boolean {
    const compareTotal = this.filteredTotal ?? this.totalCount;
    return compareTotal > 0 && compareTotal === this.selectedRowsCount;
  }

  private updateSingleSelectedRowsCount(selected: boolean): void {
    if (selected) {
      this.selectedRowsCount += 1;
    } else {
      this.selectedRowsCount -= 1;
    }
    this.selectedRowsCountChange$.next(this.selectedRowsCount);
  }

  public updateFilteredSelectedCount(
    selectedCount: number,
    filteredTotal: number,
    isFiltered: boolean,
  ): void {
    this.filteredTotal = isFiltered ? filteredTotal : null;
    this.selectedRowsCount = selectedCount;
    this.selectedRowsCountChange$.next(this.selectedRowsCount);
    this.selectAllChange$.next(
      filteredTotal > 0 && filteredTotal === selectedCount,
    );
  }

  public markDataLoaded(): void {
    this.dataLoaded = true;
  }

  public isDataLoaded(): boolean {
    return this.dataLoaded;
  }

  public persistSelection(orderIds: Array<string>): Observable<number> {
    return this.orderService.bulkSelectionChange(
      this.currentStatus as 'active' | 'inactive',
      orderIds,
    );
  }

  getTotalCount(): number {
    return this.totalCount;
  }
}
